import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { setAuthHeadersProvider, setAuthRefreshHandler } from '@/shared/api/client';
import { decodeToken, msUntilExpiry } from '@/shared/lib/jwt';
import { ensureFingerprint, getCachedFingerprint } from '@/shared/lib/fingerprint';
import { STORAGE_KEYS } from '@/shared/lib/constants';
import { env } from '@/shared/lib/env';
import * as authApi from '@/features/auth/api';
import { md5 } from '@/shared/lib/md5';
import { setAuthMirror } from '@/shared/lib/auth-state';
import { router } from '@/app/router';
import type { AssociatedTenant, AuthEnvelope, AuthState, LoginPayload } from '@/features/auth/types';

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  /** Applica un AuthEnvelope ottenuto da flussi alternativi (passkey, Google JWT). */
  loginWithEnvelope: (envelope: AuthEnvelope) => void;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  isAuthenticated: boolean;
  needsTenantSelection: boolean;
  /** Tenant id su cui sono in cross-tenant (header `tenantId`), null se opero sul mio JWT. */
  crossTenantOverride: string | null;
  /** Nome leggibile del tenant override per UI (banner, switcher). */
  crossTenantOverrideName: string | null;
  /** Attiva il cross-tenant verso un sottotenant target. */
  setCrossTenantOverride: (tenantId: string, name: string) => void;
  /** Disattiva il cross-tenant e torna sul tenant del JWT. */
  clearCrossTenantOverride: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const REFRESH_BUFFER_MS = 60_000;
const MAX_SAFE_TIMEOUT = 2_147_483_647; // setTimeout max (~24.8 days); larger values fire immediately!

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => {
    const refresh = localStorage.getItem(STORAGE_KEYS.refreshToken);
    return { token: null, refreshToken: refresh, decoded: null, user: null, associatedTenants: [] };
  });
  const [crossTenant, setCrossTenant] = useState<{ id: string | null; name: string | null }>(() => ({
    id: sessionStorage.getItem(STORAGE_KEYS.activeTenantOverride),
    name: sessionStorage.getItem(STORAGE_KEYS.activeTenantOverrideName),
  }));
  const qc = useQueryClient();
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setCrossTenantOverride = useCallback((tenantId: string, name: string) => {
    sessionStorage.setItem(STORAGE_KEYS.activeTenantOverride, tenantId);
    sessionStorage.setItem(STORAGE_KEYS.activeTenantOverrideName, name);
    setCrossTenant({ id: tenantId, name });
    // Invalida tutte le query: il cambio di tenant context cambia ogni payload.
    void qc.invalidateQueries();
    void router.invalidate();
  }, [qc]);

  const clearCrossTenantOverride = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEYS.activeTenantOverride);
    sessionStorage.removeItem(STORAGE_KEYS.activeTenantOverrideName);
    setCrossTenant({ id: null, name: null });
    void qc.invalidateQueries();
    void router.invalidate();
  }, [qc]);

  const applyAuthResponse = useCallback((env: AuthEnvelope | null) => {
    if (!env?.success || !env.data) return;
    const info = env.data;
    const token = authApi.stripBearerPrefix(info.authorizationBearer);
    const refreshToken = authApi.stripBearerPrefix(info.authorizationRefreshBearer);
    const decoded = decodeToken(token);
    if (refreshToken) localStorage.setItem(STORAGE_KEYS.refreshToken, refreshToken);
    if (info.tenantId) localStorage.setItem(STORAGE_KEYS.tenantId, info.tenantId);
    const tenants: AssociatedTenant[] = info.associatedTenants ?? [];
    setState({
      token,
      refreshToken,
      decoded,
      user: token
        ? {
            id: info.id,
            email: info.email,
            firstName: info.firstName,
            lastName: info.lastName,
          }
        : null,
      associatedTenants: tenants,
    });
  }, []);

  const doLogout = useCallback(async () => {
    try {
      if (state.token) await authApi.logout();
    } catch {
      // ignore — local cleanup is what matters
    }
    localStorage.removeItem(STORAGE_KEYS.refreshToken);
    localStorage.removeItem(STORAGE_KEYS.tenantId);
    setState({ token: null, refreshToken: null, decoded: null, user: null, associatedTenants: [] });
  }, [state.token]);

  const doRefresh = useCallback(async () => {
    const stored = state.refreshToken ?? localStorage.getItem(STORAGE_KEYS.refreshToken);
    if (!stored) {
      // no refresh token → nothing to refresh, ensure clean state
      return;
    }
    try {
      const resp = await authApi.refreshAccessToken(stored);
      applyAuthResponse(resp);
    } catch {
      await doLogout();
    }
  }, [applyAuthResponse, doLogout, state.refreshToken]);

  /**
   * Handler per il refresh-on-401 dell'HTTP client (BE v1.8.2). Ritorna il nuovo
   * access token, o null se il refresh fallisce — in quel caso facciamo logout, e
   * il successivo router.invalidate() porta l'utente al login (route guard).
   */
  const refreshForApi = useCallback(async (): Promise<string | null> => {
    const stored = state.refreshToken ?? localStorage.getItem(STORAGE_KEYS.refreshToken);
    if (!stored) {
      await doLogout();
      return null;
    }
    try {
      const resp = await authApi.refreshAccessToken(stored);
      applyAuthResponse(resp);
      return authApi.stripBearerPrefix(resp?.data?.authorizationBearer);
    } catch {
      await doLogout();
      return null;
    }
  }, [applyAuthResponse, doLogout, state.refreshToken]);

  const doLogin = useCallback(
    async (email: string, password: string) => {
      const fingerprint = await ensureFingerprint();
      const lang = localStorage.getItem(STORAGE_KEYS.lang) ?? env.VITE_DEFAULT_LANG;
      // BE Identity accepts MD5 hex when user has IsPasswordMd5=1 (most accounts on dev).
      // For new accounts created via /register-otp, BE handles plain pwd. To keep client uniform,
      // we always pass MD5 — the BE accepts both shapes for legacy users and validates server-side.
      const hashed = md5(password);
      const payload: LoginPayload = { Email: email, Password: hashed, FingerPrint: fingerprint, UserLang: lang };
      const resp = await authApi.login(payload);
      applyAuthResponse(resp);
    },
    [applyAuthResponse]
  );

  useEffect(() => {
    const tenantId = state.decoded?.tenantId ?? localStorage.getItem(STORAGE_KEYS.tenantId) ?? null;
    setAuthHeadersProvider(() => ({
      token: state.token,
      fingerprint: getCachedFingerprint(),
      tenantId,
      lang: localStorage.getItem(STORAGE_KEYS.lang) ?? env.VITE_DEFAULT_LANG,
    }));
    // Update sync mirror used by route guards (beforeLoad).
    setAuthMirror({ token: state.token, tenantId });
    // Invalidate router so active route guards re-run with the new state.
    void router.invalidate();
  }, [state.token, state.decoded]);

  // Espone il refresh-on-401 all'HTTP client (single-flight gestito lato client.ts).
  useEffect(() => {
    setAuthRefreshHandler(refreshForApi);
    return () => setAuthRefreshHandler(null);
  }, [refreshForApi]);

  // Quando cambia il JWT (login/refresh/changeTenant), droppa l'eventuale override:
  // il cross-tenant è relativo al JWT corrente, non sopravvive a un cambio di identità.
  useEffect(() => {
    if (crossTenant.id && state.decoded?.tenantId === crossTenant.id) {
      clearCrossTenantOverride();
    }
  }, [state.decoded?.tenantId, crossTenant.id, clearCrossTenantOverride]);

  useEffect(() => {
    if (refreshTimer.current) {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
    if (!state.token) return;
    const ms = msUntilExpiry(state.token) - REFRESH_BUFFER_MS;
    if (ms <= 0) {
      void doRefresh();
      return;
    }
    // Clamp to MAX_SAFE_TIMEOUT to prevent setTimeout overflow firing immediately
    // when the JWT expiry is far in the future (BE issues ~30-day tokens).
    const delay = Math.min(ms, MAX_SAFE_TIMEOUT);
    refreshTimer.current = setTimeout(() => {
      void doRefresh();
    }, delay);
    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
  }, [state.token, doRefresh]);

  useEffect(() => {
    if (!state.token && state.refreshToken) {
      void doRefresh();
    }
    void ensureFingerprint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      login: doLogin,
      loginWithEnvelope: applyAuthResponse,
      logout: doLogout,
      refresh: doRefresh,
      isAuthenticated: Boolean(state.token),
      needsTenantSelection: Boolean(state.token) && state.associatedTenants.length > 1,
      crossTenantOverride: crossTenant.id,
      crossTenantOverrideName: crossTenant.name,
      setCrossTenantOverride,
      clearCrossTenantOverride,
    }),
    [state, doLogin, applyAuthResponse, doLogout, doRefresh, crossTenant.id, crossTenant.name, setCrossTenantOverride, clearCrossTenantOverride]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Hook colocated with the provider for ergonomics; not a Fast-Refresh issue in practice.
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
